import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, TrendingUp, Target, Zap, ArrowRight, Phone, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const BookCall = () => {

  const benefits = [
    {
      icon: TrendingUp,
      title: "Pipeline Acceleration",
      description: "Build predictable, qualified pipeline that your sales team can actually close"
    },
    {
      icon: Target,
      title: "Market Positioning",
      description: "Establish authority in your industry and become the obvious choice for enterprise buyers"
    },
    {
      icon: Zap,
      title: "Revenue Growth",
      description: "Drive measurable ROI with campaigns optimized for deal size, not just lead volume"
    }
  ];

  const whatToExpect = [
    "In-depth analysis of your current market position and competitors",
    "Custom growth roadmap with clear milestones and KPIs",
    "ROI projections based on your business model and deal size",
    "Actionable strategies you can implement immediately"
  ];

  return (
    <>
      <EnhancedSEO 
        type="service"
        title="Book a Strategic Consultation | B2B Growth | VGP"
        description="Schedule a strategic consultation to discuss pipeline acceleration, demand generation, and revenue growth for your B2B business. Data-driven insights guaranteed."
        keywords="B2B strategy consultation, enterprise marketing consultation, demand generation strategy, B2B growth consultation"
        url="/book-call"
      />

      <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background">
        {/* Hero Section */}
        <section className="pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-accent to-secondary rounded-full">
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">
                Strategic Consultation
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-foreground mb-6 leading-tight">
              Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary-light to-accent">Accelerate</span> Your B2B Pipeline?
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Book a strategic consultation and discover how to generate predictable, qualified pipeline for your sales team.
            </p>

            {/* CTA Button */}
            <Button 
              variant="premium" 
              size="lg"
              className="h-16 md:h-20 px-10 md:px-14 text-xl md:text-2xl font-black shadow-2xl hover:shadow-primary/50 transform hover:scale-105 transition-all duration-300 group mb-12"
              asChild
            >
              <a 
                href="https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3"
              >
                <Phone className="w-6 h-6 md:w-8 md:h-8 animate-pulse" />
                Schedule Strategic Review
                <ArrowRight className="w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-2 transition-transform" />
              </a>
            </Button>

            {/* Transparent Value Statement - Prominent */}
            <Card className="bg-gradient-to-r from-accent/10 via-secondary/10 to-accent/10 border-2 border-accent/30 shadow-xl mb-12">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-8 h-8 text-accent flex-shrink-0 mt-1" />
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-foreground mb-3">
                      What to Expect From This Call
                    </h3>
                    <p className="text-base text-foreground/80 leading-relaxed">
                      This is a strategic discussion about your growth objectives. We'll analyze your current market position, identify opportunities, and outline a data-driven roadmap to accelerate pipeline. <span className="font-bold text-primary underline decoration-primary/50">Whether we partner or not</span>, you'll leave with actionable insights and a clear strategy you can implement immediately. <span className="font-bold text-foreground">No generic advice. Specific to your business.</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Calendly CTA Section - PROMINENT */}
        <section className="py-16 px-6 bg-gradient-to-b from-primary/10 via-accent/5 to-transparent">
          <div className="container mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-3 mb-6">
              <Calendar className="w-10 h-10 text-primary" />
              <h2 className="text-3xl md:text-5xl font-black text-foreground">
                Book Your Strategic Consultation
              </h2>
            </div>
            <p className="text-xl text-muted-foreground mb-10">
              45-minute consultation • Confidential • Guaranteed insights
            </p>
            
            {/* MASSIVE CTA Button */}
            <Button 
              variant="premium" 
              size="lg"
              className="h-20 md:h-24 px-12 md:px-16 text-2xl md:text-3xl font-black shadow-2xl hover:shadow-primary/50 transform hover:scale-105 transition-all duration-300 group relative overflow-hidden"
              asChild
            >
              <a 
                href="https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-4"
              >
                <Phone className="w-8 h-8 md:w-10 md:h-10 animate-pulse" />
                Schedule Consultation
                <ArrowRight className="w-8 h-8 md:w-10 md:h-10 group-hover:translate-x-2 transition-transform" />
              </a>
            </Button>
            <p className="text-sm text-muted-foreground mt-6">
              Opens in Calendly to select your preferred time
            </p>
          </div>
        </section>

        {/* What You'll Get Section */}
        <section className="py-16 px-6 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-5xl font-black text-center text-foreground mb-12">
              What You'll Discover
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card key={index} className="bg-background border-2 hover:border-primary/50 transition-all hover:shadow-xl">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icon className="w-8 h-8 text-primary-foreground" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-3">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {benefit.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="bg-background">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
                  During Your Consultation
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {whatToExpect.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                      <p className="text-foreground/90">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Social Proof Quick Stats */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-2">
                  £2.4M+
                </div>
                <div className="text-sm text-muted-foreground font-semibold">Avg Annual Pipeline</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-2">
                  340%
                </div>
                <div className="text-sm text-muted-foreground font-semibold">Deal Size Increase</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-2">
                  45%
                </div>
                <div className="text-sm text-muted-foreground font-semibold">Shorter Sales Cycle</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-2">
                  200+
                </div>
                <div className="text-sm text-muted-foreground font-semibold">B2B Clients</div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-primary via-primary-light to-primary">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
              Stop Losing Deals to Better-Positioned Competitors
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-2xl mx-auto">
              Every quarter without a strong growth engine is pipeline your competitors are capturing instead.
            </p>

            {/* Scroll to Calendly Button */}
            <Button 
              variant="secondary" 
              size="lg"
              className="h-20 md:h-24 px-12 md:px-16 text-2xl md:text-3xl font-black shadow-2xl hover:shadow-accent/50 transform hover:scale-105 transition-all duration-300 group mb-8"
              onClick={() => {
                document.querySelector('iframe')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              <Phone className="w-8 h-8 md:w-10 md:h-10 animate-pulse" />
              Schedule Strategic Review
              <ArrowRight className="w-8 h-8 md:w-10 md:h-10 group-hover:translate-x-2 transition-transform" />
            </Button>

            <p className="text-white/80 text-sm mb-8">
              Confidential discussion • No obligation • Typically responds within 24 hours
            </p>

            <div className="text-white/70 text-sm">
              Want to learn more first? <Link to="/enterprise" className="underline hover:text-white font-semibold">Enterprise solutions</Link> or <Link to="/pricing" className="underline hover:text-white font-semibold">view packages</Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BookCall;
